/**
 * Générateur — Automatisme : calculer une moyenne pour un très petit nombre
 * de valeurs.
 * Attendu ID : 1125 | Niveau : 4ème | Domaine : Statistiques
 * Rubrique : AUTOMATISME (programme 2026, Statistiques 4e) — rituel court.
 * Labels (cartes exerciseur) :
 *   - Calculer la moyenne             (saisie ; QCM à leurres calculés)
 *   - Retrouver le total            (saisie : total connaissant la moyenne, multiplication a trou)
 *   - La moyenne, vrai ou faux ?      (V/F sur une petite série affichée)
 *   - Comment calcule-t-on une moyenne ? (V/F de pur rappel, réactivation)
 *
 * ── Plan de cartes validé (2026-09-07) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Statistiques 4e, automatisme « Calculer une
 * moyenne pour un très petit nombre de valeurs » + grille P1-P12 (BO cycle 3
 * 2025, cycle 4 2026). P1 : absente du cycle 3, INTRODUITE en 5e (objectif
 * « calculer et interpréter la moyenne simple »), automatisme en 4e sur un très
 * petit nombre de valeurs. P2 : sens installé — somme des valeurs ÷ nombre de
 * valeurs, valeur de répartition égale ; non installés (distracteurs) —
 * moyenne pondérée, médiane, valeur la plus fréquente, moyenne prise pour une
 * valeur de la série. P3 : 2 à 4 valeurs entières, ou deux valeurs à une
 * décimale ; somme ≤ 80 ; quotient exact ou demi. P5 : liste, tableau simple. P6 : aucune
 * calculatrice (flag à trancher par format). P7 : jamais de valeur répétée
 * avec son effectif (moyenne pondérée = objectif 4e). P8 : moyenne, série,
 * valeur, total, nombre de valeurs ; pas « effectif », « pondérée »,
 * « médiane ». P9 : additions de décimaux, division par 2, 3, 4, 5 et
 * multiplication à trou restent implicites. P10 : constat. P11 : diviser par
 * 2 quel que soit le nombre de valeurs ; la moyenne serait une des valeurs ;
 * la moyenne serait le milieu de la plus petite et de la plus grande ; oublier
 * une valeur nulle. P12 : notion vue une seule fois (5e), rémanence faible →
 * carte de réactivation.
 * INVARIANTS :
 *   - la série est CONSTRUITE depuis la moyenne cible (écarts de somme nulle)
 *     → le quotient est toujours exact ou un demi, jamais de reste ;
 *   - toutes les valeurs sont positives, distinctes, dans le champ du contexte ;
 *   - leurres de QCM calculés (somme, somme ÷ 2, milieu, une valeur) et
 *     vérifiés distincts ; V/F : pas de crochets, mixité 1-3 faux, familles
 *     exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { tableEnonce, ligneTable } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 1125;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CALCUL = 'Calculer la moyenne';
const L_MANQUANTE = 'Retrouver le total';
const L_SERIE = 'La moyenne, vrai ou faux ?';
const L_RAPPEL = 'Comment calcule-t-on une moyenne ?';

const fr = (x) => String(Math.round(x * 100) / 100).replace('.', ',');
const R1 = (x) => Math.round(x * 10) / 10;

// ─── Contextes ─────────────────────────────────────────────────────────────
// min/max : bornes des valeurs ; dec : valeurs à une décimale ; unite : suffixe affiché.
const CONTEXTES = [
    { id: 'notes', min: 4, max: 20, dec: false, unite: '', mMin: 8, mMax: 16,
      serie: (p, n) => `Voici les ${n} notes sur 20 de ${p} ce trimestre`, q: 'Quelle est sa moyenne ?', titre: 'Notes' },
    { id: 'temperatures', min: 6, max: 30, dec: true, unite: '°C', mMin: 10, mMax: 25,
      serie: (p, n) => `Voici les températures relevées à ${p} sur ${n} jours`, q: 'Quelle est la température moyenne ?', titre: 'Températures (°C)' },
    { id: 'distances', min: 2, max: 30, dec: true, unite: 'km', mMin: 5, mMax: 20,
      serie: (p, n) => `Voici les distances parcourues par ${p} lors de ${n} sorties à vélo`, q: 'Quelle est la distance moyenne par sortie ?', titre: 'Distances (km)' },
    // Revue 2026-09-07 (A8) : plage élargie, sinon à 4 valeurs la garde « somme ≤ 80 » imposait la moyenne 20.
    { id: 'durees', min: 5, max: 60, dec: false, unite: 'min', mMin: 12, mMax: 45,
      serie: (p, n) => `Voici les durées de trajet de ${p} sur ${n} jours`, q: 'Quelle est la durée moyenne de trajet ?', titre: 'Durées (min)' },
    { id: 'prix', min: 2, max: 20, dec: true, unite: '€', mMin: 5, mMax: 15,
      serie: (p, n) => `Voici les prix des ${n} articles achetés par ${p}`, q: 'Quel est le prix moyen d\'un article ?', titre: 'Prix (€)' },
];
const PRENOMS = ['Lina', 'Léo', 'Maya', 'Noah', 'Inès', 'Sami', 'Chloé', 'Yanis', 'Jade', 'Enzo'];
const VILLES = ['Brest', 'Lyon', 'Lille', 'Nice', 'Pau', 'Metz'];
const sujet = (ctx) => (ctx.id === 'temperatures' ? randomChoice(VILLES) : randomChoice(PRENOMS));

const avecUnite = (v, ctx) => (ctx.unite ? `${fr(v)} ${ctx.unite}` : fr(v));
const listeInline = (vals, ctx) => vals.map((v) => avecUnite(v, ctx)).join(' ; ');

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Valeurs fausses nommées d'un calcul de moyenne : les formules des leurres du QCM (arrondies comme eux au dixième, sauf pour la saisie).
const erreursMoyenne = (vals, somme, n, arrondir = R1) => [
    ['divise_par_2', arrondir(somme / 2)],
    ['milieu_des_extremes', arrondir((Math.min(...vals) + Math.max(...vals)) / 2)],
    ['moyenne_une_des_valeurs', Math.max(...vals)],
    ['moyenne_une_des_valeurs', Math.min(...vals)],
    ['nombre_de_valeurs_faux', arrondir(somme / (n + 1))],
    ['total_pas_divise', somme],
];
/** Les erreurs sûres : ni la bonne réponse, ni une valeur portée par deux codes. */
const erreursSures = (paires, juste) => {
    const parValeur = [];
    paires.forEach(([code, v]) => {
        if (!Number.isFinite(v) || Math.abs(v - juste) < 1e-9) return;
        const deja = parValeur.find((p) => Math.abs(p.v - v) < 1e-9);
        if (deja) deja.codes.add(code); else parValeur.push({ v, codes: new Set([code]) });
    });
    return parValeur.filter((p) => p.codes.size === 1).map((p) => ({ code: [...p.codes][0], v: p.v }));
};
const auCentiemePres = (v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-9;

/**
 * Série de n valeurs (2 ≤ n ≤ 4) de moyenne m EXACTE : écarts de somme nulle.
 * Garde calcul mental (décision prof, vague 5e : rien de trop lourd de tête) :
 *   - m entier, ou demi quand n = 2 ;
 *   - une décimale SEULEMENT à deux valeurs (12,4 et 13,6 → 13) ; à 3 ou 4
 *     valeurs, tout est entier même dans un contexte décimal (17 °C ; 21 °C ; 19 °C) ;
 *   - somme ≤ 80.
 * Valeurs distinctes, positives, dans [min ; max]. `entier` force des valeurs entières.
 */
const tirerSerie = (ctx, n, entier = false) => {
    for (let essai = 0; essai < 200; essai++) {
        const m = randInt(ctx.mMin, ctx.mMax) + (n === 2 && !entier && Math.random() < 0.3 ? 0.5 : 0);
        const decimale = ctx.dec && n === 2 && !entier;
        let vals;
        if (decimale) {
            const d = randInt(2, 25) / 10;                                 // 0,2 à 2,5, jamais un entier d'écart
            if (Number.isInteger(d)) continue;
            vals = [R1(m - d), R1(m + d)];
        } else {
            const pas = Number.isInteger(m) ? 1 : 0.5;
            const amp = Math.min(6, Math.floor((ctx.max - ctx.min) / 3));
            const ecarts = [];
            for (let i = 0; i < n - 1; i++) ecarts.push(randInt(-amp, amp));
            ecarts.push(-ecarts.reduce((a, b) => a + b, 0));
            if (ecarts.every((e) => e === 0)) continue;
            vals = ecarts.map((e) => R1(m + e * pas));
        }
        if (vals.some((v) => v < ctx.min || v > ctx.max)) continue;
        if (new Set(vals.map(fr)).size !== n) continue;
        const somme = R1(vals.reduce((a, b) => a + b, 0));
        if (somme > 80) continue;
        return { vals: shuffle(vals), m, somme };
    }
    return null;
};

const tirerContexteSerie = (ns = [2, 3, 4], entier = false) => {
    for (let essai = 0; essai < 50; essai++) {
        const ctx = randomChoice(CONTEXTES); const n = randomChoice(ns);
        const s = tirerSerie(ctx, n, entier);
        if (s) return { ctx, n, ...s, p: sujet(ctx) };
    }
    throw new Error('gen_1125 : série introuvable');
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refC = { v: null }; const refQ = { v: null }; const refM = { v: null }; const refS = { v: null };

// ─── Carte 1a : calculer la moyenne (saisie) ───────────────────────────────

const construireCalculSaisie = () => {
    const T = tirerContexteSerie();
    const texte = `<p>${T.ctx.serie(T.p, T.n)} : ${listeInline(T.vals, T.ctx)}.</p><p>${T.ctx.q}</p>`;
    const payload = makeSaisie(fr(T.m), texte, { ...opts(L_CALCUL), suffixe: T.ctx.unite, });
    payload.contenu = { ...payload.contenu, sousType: `n${T.n}` };
    payload._meta.params = { ctx: T.ctx.id, n: T.n, vals: T.vals, m: T.m, signature: `${T.ctx.id}-${T.vals.map(fr).join('x')}` };
    // Valeurs exactes seulement (une division qui ne tombe pas juste au centième n'est pas rangée).
    const erreurs = erreursSures(erreursMoyenne(T.vals, T.somme, T.n, (x) => x), T.m).filter((e) => auCentiemePres(e.v)).map((e) => ({ code: e.code, valeur: fr(e.v) }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genCalculSaisie = () => antiRepeat(construireCalculSaisie, refC);

// ─── Carte 1b : calculer la moyenne (QCM à leurres calculés) ──────────────

const construireCalculQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const T = tirerContexteSerie();
        const { vals, m, somme, n } = T;
        const mn = Math.min(...vals); const mx = Math.max(...vals);
        const pool = [
            { v: R1(somme / 2), role: 'divise_par_2' },                    // divise par 2 quel que soit n (mort si n = 2)
            { v: R1((mn + mx) / 2), role: 'milieu' },                      // milieu de la plus petite et de la plus grande
            { v: mx, role: 'une_valeur' },                                 // la moyenne serait une valeur de la série
            { v: mn, role: 'une_valeur_min' },
            { v: R1(somme / (n + 1)), role: 'divise_par_n_plus_1' },       // oublie une valeur dans le compte
            { v: R1(m + randomChoice([-1, 1])), role: 'voisin' },          // erreur de calcul d'une unité
        ];
        const ok = (v) => Math.abs(v - m) > 1e-9 && v > 0 && Math.abs(v * 10 - Math.round(v * 10)) < 1e-9;
        const cands = []; shuffle(pool).forEach((c) => { if (ok(c.v) && !cands.some((l) => Math.abs(l.v - c.v) < 1e-9)) cands.push(c); });
        // Revue 2026-09-07 (A2) : la somme (oubli de diviser) est TOUJOURS là ; les deux autres leurres sont choisis
        // pour que les formes soient homogènes : jamais une seule option à virgule, jamais une seule option entière.
        const estDec = (v) => !Number.isInteger(v);
        let leurres = null;
        for (let a = 0; a < cands.length && !leurres; a++) {
            for (let b = a + 1; b < cands.length && !leurres; b++) {
                const nbDec = [m, somme, cands[a].v, cands[b].v].filter(estDec).length;
                if (nbDec !== 1 && nbDec !== 3) leurres = [{ v: somme, role: 'somme' }, cands[a], cands[b]];
            }
        }
        if (!leurres) continue;
        const options = shuffle([m, ...leurres.map((l) => l.v)].map((v) => avecUnite(v, T.ctx)));
        const texte = `<p>${T.ctx.serie(T.p, n)} : ${listeInline(vals, T.ctx)}.</p><p>${T.ctx.q}</p>`;
        const payload = makeQcm(options, avecUnite(m, T.ctx), texte, opts(L_CALCUL));
        payload.contenu = { ...payload.contenu, sousType: `n${n}` };
        payload._meta.params = { ctx: T.ctx.id, n, vals, m, roles: leurres.map((l) => l.role), signature: `${T.ctx.id}-${vals.map(fr).join('x')}` };
        const erreurs = erreursSures(erreursMoyenne(vals, somme, n), m).map((e) => ({ code: e.code, valeur: avecUnite(e.v, T.ctx) })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1125 : QCM moyenne sans leurres');
};
const genCalculQcm = () => antiRepeat(construireCalculQcm, refQ);

// ─── Carte 2 : retrouver le total ou une valeur manquante (saisie) ─────────

// (Le cas « valeur manquante » a été retiré à la demande du prof, 2026-09-07 : remonter la formule
// en deux étapes relève des objectifs de 4e « problèmes faisant intervenir les indicateurs », pas de l'automatisme.)
const construireManquante = () => {
    const T = tirerContexteSerie();
    const { vals, m, somme, n, ctx } = T;
    const texte = `<p>${ctx.serie(T.p, n).replace(/^Voici les /, 'On connaît la moyenne des ')} : elle vaut ${avecUnite(m, ctx)}.</p><p>Quel est le total de ces ${n} valeurs ?</p>`;
    const payload = makeSaisie(fr(somme), texte, { ...opts(L_MANQUANTE), suffixe: ctx.unite });
    payload.contenu = { ...payload.contenu, sousType: 'total' };
    payload._meta.params = { ctx: ctx.id, n, vals, m, reponse: fr(somme), signature: `total-${ctx.id}-${n}-${fr(m)}` };
    // Moyenne et nombre de valeurs additionnés, « ÷ 2 » retourné en « × 2 », ou division refaite au lieu d'être défaite.
    const erreurs = erreursSures([['addition_pour_produit', m + n], ['divise_par_2', m * 2], ['operation_directe', m / n]], somme)
        .filter((e) => auCentiemePres(e.v)).map((e) => ({ code: e.code, valeur: fr(e.v) }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genManquante = () => antiRepeat(construireManquante, refM);

// ─── Carte 3 : la moyenne, vrai ou faux ? (série affichée) ─────────────────

let dernieresLignesSerie1125 = [];

const genSerieVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const T = tirerContexteSerie();
        const { vals, m, somme, n, ctx } = T;
        const mn = Math.min(...vals); const mx = Math.max(...vals);
        const fauxM = shuffle([R1(somme / 2), R1((mn + mx) / 2), somme].filter((v) => Math.abs(v - m) > 1e-9 && Math.abs(v * 10 - Math.round(v * 10)) < 1e-9))[0];
        const fauxTotal = R1(somme + randomChoice([-2, -1, 1, 2]) * (ctx.dec ? 0.5 : 1));
        const estUneValeur = vals.some((v) => Math.abs(v - m) < 1e-9);
        const candidates = [
            { l: `La moyenne de cette série est ${avecUnite(m, ctx)}.`, v: true, f: 'moyenne' },
            ...(fauxM !== undefined ? [{ l: `La moyenne de cette série est ${avecUnite(fauxM, ctx)}.`, v: false, f: 'moyenne' }] : []),
            { l: `Le total des ${n} valeurs est ${avecUnite(somme, ctx)}.`, v: true, f: 'total' },
            { l: `Le total des ${n} valeurs est ${avecUnite(fauxTotal, ctx)}.`, v: false, f: 'total' },
            { l: `La moyenne est l'une des valeurs de la série.`, v: estUneValeur, f: 'position' },
            // Revue 2026-09-07 (A2) : l'encadrement est pris entre deux valeurs de la série, qui encadrent
            // la moyenne ou non (« entre 7 et 11 » quand la moyenne vaut 12) : toujours vrai avant.
            ...(() => { const tri = [...vals].sort((a, b) => a - b); const a = randInt(0, n - 2); const b = randInt(a + 1, n - 1);
                return [{ l: `La moyenne est comprise entre ${avecUnite(tri[a], ctx)} et ${avecUnite(tri[b], ctx)}.`, v: tri[a] <= m && m <= tri[b], f: 'position' }]; })(),
            { l: `Pour trouver la moyenne, on divise le total par ${n}.`, v: true, f: 'diviseur' },
            ...(n !== 2 ? [{ l: `Pour trouver la moyenne, on divise le total par 2.`, v: false, f: 'diviseur' }] : []),
            { l: `Multiplier la moyenne par ${n} redonne le total des valeurs.`, v: true, f: 'retour' },
            { l: `Multiplier la moyenne par 2 redonne le total des valeurs.`, v: n === 2, f: 'retour' },
        ];
        const fausses = shuffle(candidates.filter((it) => !it.v)); const vraies = shuffle(candidates.filter((it) => it.v));
        const nbFaux = randInt(1, Math.min(3, fausses.length));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4) continue;
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesSerie1125 = [];
        if (lignes.some((it) => dernieresLignesSerie1125.includes(it.l))) continue;
        dernieresLignesSerie1125 = lignes.map((it) => it.l);
        const table = tableEnonce(ligneTable(ctx.titre, vals.map(fr)));
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            `<p>${ctx.serie(T.p, n)}.</p>${table}<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            opts(L_SERIE),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { ctx: ctx.id, n, vals, m };
        // La fausse moyenne est nommée par sa valeur ; « une des valeurs » et les règles « par 2 » par leur forme.
        const codeFauxM = (erreursSures(erreursMoyenne(vals, somme, n), m).find((e) => Math.abs(e.v - fauxM) < 1e-9) || {}).code;
        const codeLigne = (it) => {
            if (it.f === 'moyenne') return codeFauxM;
            if (it.l === 'La moyenne est l\'une des valeurs de la série.') return 'moyenne_une_des_valeurs';
            return it.f === 'diviseur' || it.f === 'retour' ? 'divise_par_2' : null;
        };
        const erreurs = lignes.filter((it) => !it.v && codeLigne(it)).map((it) => ({ code: codeLigne(it), ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1125 : V/F série sans tirage après 160 essais');
};

// ─── Carte 4 : comment calcule-t-on une moyenne ? (V/F de pur rappel) ──────

// Familles exclusives : deux lignes d'une même famille se contredisent ou se déduisent l'une de l'autre.
const POOL_RAPPEL = [
    { l: 'Pour calculer une moyenne, on additionne toutes les valeurs, puis on divise par le nombre de valeurs.', v: true, f: 'definition' },
    { l: 'Pour calculer une moyenne, on additionne toutes les valeurs, puis on divise toujours par 2.', v: false, f: 'definition', code: 'divise_par_2' },
    { l: 'Pour calculer une moyenne, on prend la valeur qui revient le plus souvent.', v: false, f: 'definition', code: 'moyenne_une_des_valeurs' },
    { l: 'Pour calculer une moyenne, on additionne la plus petite et la plus grande valeur, puis on divise par 2.', v: false, f: 'definition', code: 'milieu_des_extremes' },
    { l: 'La moyenne de deux valeurs est le nombre situé exactement au milieu de ces deux valeurs.', v: true, f: 'deux' },
    { l: 'La moyenne de deux valeurs est toujours la plus grande des deux.', v: false, f: 'deux', code: 'moyenne_une_des_valeurs' },
    { l: 'La moyenne d\'une série est toujours comprise entre la plus petite et la plus grande valeur.', v: true, f: 'position' },
    { l: 'La moyenne d\'une série est toujours égale à l\'une des valeurs de la série.', v: false, f: 'position', code: 'moyenne_une_des_valeurs' },
    { l: 'La moyenne d\'une série peut être plus grande que toutes ses valeurs.', v: false, f: 'position' },
    { l: 'Si toutes les valeurs d\'une série sont égales, la moyenne est égale à cette valeur.', v: true, f: 'egales' },
    { l: 'Si toutes les valeurs d\'une série sont égales, la moyenne vaut 0.', v: false, f: 'egales' },
    { l: 'La moyenne multipliée par le nombre de valeurs donne le total des valeurs.', v: true, f: 'total' },
    { l: 'La moyenne multipliée par le nombre de valeurs donne la plus grande valeur.', v: false, f: 'total' },
    { l: 'Une valeur égale à 0 compte dans le nombre de valeurs quand on calcule la moyenne.', v: true, f: 'zero' },
    { l: 'Une valeur égale à 0 ne compte pas dans le nombre de valeurs quand on calcule la moyenne.', v: false, f: 'zero', code: 'nombre_de_valeurs_faux' },
];

let dernieresLignesRappel1125 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1125 = [];
        if (lignes.some((it) => dernieresLignesRappel1125.includes(it.l))) continue;
        dernieresLignesRappel1125 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur la moyenne d\'une série de valeurs, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1125 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    calcul_saisie: genCalculSaisie,
    calcul_qcm_unique: genCalculQcm,
    total_saisie: genManquante,
    serie_tableau_vf: genSerieVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'total', label: L_MANQUANTE, formatsList: buildFormats('total') },
    { id: 'serie', label: L_SERIE, formatsList: buildFormats('serie') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
